import { MinusCircleIcon, PlusCircleIcon } from "lucide-react";
import type { CampaignLocationT } from "./campaign-location-list";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

interface LocationDropdownItemProps {
  location: CampaignLocationT;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Individual location dropdown item component.
 * Displays location information in an expandable accordion format.
 */
export default function LocationDropdownItem({
  location,
  isExpanded,
  onToggle,
}: LocationDropdownItemProps) {
  const { isAudi } = useBrand();

  return (
    <div
      className={cn(
        "bg-background overflow-hidden rounded-lg shadow-lg transition-shadow",
        isAudi && "bg-card rounded-[20px] shadow-none"
      )}
    >
      <button
        onClick={onToggle}
        className={cn(
          "hover:bg-muted flex w-full cursor-pointer items-center justify-between p-4 text-left transition-colors",
          isAudi && "hover:bg-accent"
        )}
        aria-expanded={isExpanded}
        aria-controls={`location-content-${location.id}`}
      >
        <div className="flex min-h-8 flex-col justify-center gap-1">
          <span className="text-foreground text-base font-medium">
            {location.location_name || location.name}
          </span>
        </div>
        <div
          className={cn(
            "text-primary flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-colors",
            isAudi && "text-muted-foreground"
          )}
        >
          {isExpanded ? (
            <MinusCircleIcon className="h-6 w-6" />
          ) : (
            <PlusCircleIcon className="h-6 w-6" />
          )}
        </div>
      </button>

      {/* Expanded content with animation */}
      <div
        id={`location-content-${location.id}`}
        className={`grid transition-all duration-300 ease-out ${
          isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              "bg-background border-t px-4 py-4",
              isAudi && "bg-card"
            )}
          >
            {location.make_areas &&
              location.make_areas.length > 0 &&
              !isAudi && (
                <div className="text-foreground mb-3 text-base font-semibold">
                  {location.make_areas.join(" | ")}
                </div>
              )}
            <div className="text-foreground space-y-2 text-sm">
              <div>{location.company_name}</div>
              <div>{location.address}</div>
              <div>
                {location.zip_code} {location.city}
              </div>
              <div>
                Telefon:{" "}
                <a
                  href={`tel:${location.phone}`}
                  className="text-primary hover:text-primary/80 underline transition-colors"
                >
                  {location.phone}
                </a>
              </div>
              {location.telefax && <div>Telefax: {location.telefax}</div>}
              <div>
                E-Mail:{" "}
                <a
                  href={`mailto:${location.email}`}
                  className="text-primary hover:text-primary/80 underline transition-colors"
                >
                  {location.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
